import { memo } from 'react';
import { Group, Circle, Ellipse, Line, Rect } from 'react-konva';

/**
 * Singer Girl - blonde girl with microphone, casual outfit, 
 * clear face, ponytail, singing pose.
 */
export const SingerGirl = memo(function SingerGirl({ x, y, scaleX, scaleY, rotation, opacity, fill, customColors, leftArmAngle, rightArmAngle, leftLegAngle, rightLegAngle, onClick, onDragEnd, onTransformEnd, draggable, isSelected }) {
  const c = customColors || {};
  const skinColor = c.skinColor || '#fde8d0';
  const hairColor = c.hairColor || '#f5d060';
  const hairDark = '#d4a830';
  const topColor = c.dressColor || fill || '#8b5cf6';
  const skirtColor = c.skirtColor || '#6366f1';
  const shoeColor = c.shoeColor || '#4f46e5';
  const eyeColor = c.eyeColor || '#10b981';
  const micColor = '#6b7280';
  const micHead = '#374151';

  return (
    <Group
      x={x} y={y}
      scaleX={scaleX || 1} scaleY={scaleY || 1}
      rotation={rotation || 0}
      opacity={opacity ?? 1}
      draggable={draggable}
      onClick={() => onClick?.()}
      onTap={() => onClick?.()}
      onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={(e) => {
        const node = e.target;
        onTransformEnd?.({
          x: node.x(), y: node.y(),
          scaleX: node.scaleX(), scaleY: node.scaleY(),
          rotation: node.rotation(),
        });
      }}
    >
      {/* Shadow */}
      <Ellipse x={0} y={72} radiusX={20} radiusY={5} fill="#000" opacity={0.15} />

      {/* Left leg - animated */}
      <Group rotation={leftLegAngle || 0} x={-5} y={42}>
        <Line points={[0,0, -1,16]} stroke={skinColor} strokeWidth={5} lineCap="round" />
        <Rect x={-6} y={16} width={12} height={6} fill={shoeColor} cornerRadius={3} />
      </Group>

      {/* Right leg - animated */}
      <Group rotation={rightLegAngle || 0} x={5} y={42}>
        <Line points={[0,0, 1,16]} stroke={skinColor} strokeWidth={5} lineCap="round" />
        <Rect x={-5} y={16} width={12} height={6} fill={shoeColor} cornerRadius={3} />
      </Group>

      {/* Skirt */}
      <Line points={[-16,38, -12,30, 12,30, 16,38]} fill={skirtColor} closed={true} tension={0.1} />
      <Line points={[-18,42, -16,38, 16,38, 18,42]} fill={skirtColor} closed={true} tension={0.1} />
      {/* Skirt line */}
      <Line points={[-16,38, 0,40, 16,38]} stroke={topColor} strokeWidth={0.8} opacity={0.5} lineCap="round" tension={0.3} />

      {/* Body / top */}
      <Rect x={-11} y={10} width={22} height={22} fill={topColor} cornerRadius={4} />
      {/* Star decoration on shirt */}
      <Line points={[0,16, 1.5,20, 5,21, 2.5,24, 3,28, 0,26, -3,28, -2.5,24, -5,21, -1.5,20]}
        fill="#fde68a" closed={true} opacity={0.5} />

      {/* Left arm - animated */}
      <Group rotation={leftArmAngle || 0} x={-11} y={16}>
        <Line points={[0,0, -9,6, -13,2]} stroke={skinColor} strokeWidth={4.5} lineCap="round" lineJoin="round" />
        <Circle x={-13} y={2} radius={3} fill={skinColor} />
      </Group>

      {/* Right arm - animated (holding mic) */}
      <Group rotation={rightArmAngle || 0} x={11} y={16}>
        <Line points={[0,0, 7,-6, 9,-16]} stroke={skinColor} strokeWidth={4.5} lineCap="round" lineJoin="round" />
        <Circle x={9} y={-16} radius={3} fill={skinColor} />
        {/* Microphone */}
        <Line points={[9,-16, 9,-24]} stroke={micColor} strokeWidth={3} lineCap="round" />
        <Ellipse x={9} y={-28} radiusX={4} radiusY={5} fill={micHead} />
        <Line points={[6,-28, 12,-28]} stroke={micColor} strokeWidth={0.5} />
        <Line points={[6,-26, 12,-26]} stroke={micColor} strokeWidth={0.5} />
        <Line points={[6,-30, 12,-30]} stroke={micColor} strokeWidth={0.5} />
      </Group>

      {/* Neck */}
      <Rect x={-3} y={2} width={6} height={10} fill={skinColor} />
      {/* Necklace */}
      <Line points={[-5,8, 0,11, 5,8]} stroke="#fbbf24" strokeWidth={1} lineCap="round" tension={0.3} />
      <Circle x={0} y={11.5} radius={1.5} fill="#fbbf24" />

      {/* Head */}
      <Circle x={0} y={-10} radius={16} fill={skinColor} />

      {/* Hair - blonde, pulled back into ponytail, clear face */}
      <Ellipse x={0} y={-18} radiusX={17} radiusY={10} fill={hairColor} />
      {/* Hair sides - not covering face */}
      <Ellipse x={-14} y={-12} radiusX={4} radiusY={7} fill={hairColor} rotation={-10} />
      <Ellipse x={14} y={-12} radiusX={4} radiusY={7} fill={hairColor} rotation={10} />

      {/* Ponytail flowing right */}
      <Ellipse x={14} y={-20} radiusX={8} radiusY={5} fill={hairColor} rotation={-30} />
      <Ellipse x={20} y={-16} radiusX={10} radiusY={5} fill={hairColor} rotation={-45} />
      <Ellipse x={24} y={-10} radiusX={8} radiusY={4} fill={hairDark} rotation={-50} opacity={0.4} />
      {/* Ponytail band */}
      <Circle x={12} y={-20} radius={2.5} fill="#ec4899" />

      {/* Cheeks */}
      <Ellipse x={-9} y={-4} radiusX={4} radiusY={3} fill="#fca5a5" opacity={0.25} />
      <Ellipse x={9} y={-4} radiusX={4} radiusY={3} fill="#fca5a5" opacity={0.25} />

      {/* Eyes */}
      <Ellipse x={-6} y={-11} radiusX={4} radiusY={4.5} fill="white" />
      <Circle x={-6} y={-10} radius={2.5} fill={eyeColor} />
      <Circle x={-5} y={-11} radius={1} fill="white" />
      <Ellipse x={6} y={-11} radiusX={4} radiusY={4.5} fill="white" />
      <Circle x={6} y={-10} radius={2.5} fill={eyeColor} />
      <Circle x={7} y={-11} radius={1} fill="white" />

      {/* Eyelashes */}
      <Line points={[-10,-13, -9,-15]} stroke="#333" strokeWidth={0.8} lineCap="round" />
      <Line points={[-3,-14, -3,-16]} stroke="#333" strokeWidth={0.8} lineCap="round" />
      <Line points={[3,-14, 3,-16]} stroke="#333" strokeWidth={0.8} lineCap="round" />
      <Line points={[10,-13, 9,-15]} stroke="#333" strokeWidth={0.8} lineCap="round" />

      {/* Eyebrows */}
      <Line points={[-9,-15, -6,-16.5, -3,-15]} stroke={hairDark} strokeWidth={1} lineCap="round" tension={0.4} />
      <Line points={[3,-15, 6,-16.5, 9,-15]} stroke={hairDark} strokeWidth={1} lineCap="round" tension={0.4} />

      {/* Nose */}
      <Circle x={0} y={-6} radius={1} fill="#e8c4a0" />

      {/* Mouth - singing open mouth */}
      <Ellipse x={0} y={-1} radiusX={4} radiusY={3} fill="#dc2626" opacity={0.6} />
      <Line points={[-3,-2, 0,-0.5, 3,-2]} stroke="#f87171" strokeWidth={1} lineCap="round" tension={0.3} />

      {/* Music notes floating */}
      <Line points={[28,-18, 28,-26]} stroke="#c084fc" strokeWidth={1.5} lineCap="round" />
      <Circle x={26} y={-18} radius={2.5} fill="#c084fc" />
      <Line points={[28,-26, 33,-28]} stroke="#c084fc" strokeWidth={1.5} lineCap="round" />

      <Line points={[34,-8, 34,-16]} stroke="#a78bfa" strokeWidth={1.5} lineCap="round" opacity={0.6} />
      <Circle x={32} y={-8} radius={2} fill="#a78bfa" opacity={0.6} />

      {/* Selection */}
      {isSelected && <Circle x={0} y={20} radius={55} stroke="#8b5cf6" strokeWidth={1.5} dash={[6,4]} fill="transparent" opacity={0.4} />}
    </Group>
  );
});
